import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { FooterSettings, FooterLink } from "@/types/footer.types";
import { useToast } from "@/components/ui/use-toast";

export function useFooterSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch footer settings
  const { data: footerSettings, isLoading: isLoadingSettings } = useQuery({
    queryKey: ['footer-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_settings')
        .select('*')
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('Error fetching footer settings:', error);
        return null;
      }
      return data as FooterSettings | null;
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch footer links
  const { data: footerLinks = [], isLoading: isLoadingLinks } = useQuery({
    queryKey: ['footer-links'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_links')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) {
        console.error('Error fetching footer links:', error);
        return [];
      }
      return data as FooterLink[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Fetch all links (including inactive) for admin
  const { data: allFooterLinks = [], isLoading: isLoadingAllLinks } = useQuery({
    queryKey: ['footer-links-all'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('footer_links')
        .select('*')
        .order('section')
        .order('display_order');

      if (error) {
        console.error('Error fetching all footer links:', error);
        return [];
      }
      return data as FooterLink[];
    },
    staleTime: 5 * 60 * 1000,
  });

  // Update footer settings
  const updateSettingsMutation = useMutation({
    mutationFn: async (settings: Partial<FooterSettings>) => {
      const { data: existing } = await supabase
        .from('footer_settings')
        .select('id')
        .limit(1)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from('footer_settings')
          .update(settings)
          .eq('id', existing.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('footer_settings')
          .insert(settings);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-settings'] });
      toast({
        title: "Configurações salvas",
        description: "As configurações do rodapé foram atualizadas.",
      });
    },
    onError: (error) => {
      console.error('Error updating footer settings:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar as configurações.",
        variant: "destructive",
      });
    },
  });

  // Add footer link
  const addLinkMutation = useMutation({
    mutationFn: async (link: Omit<FooterLink, 'id' | 'created_at' | 'updated_at'>) => {
      const { error } = await supabase
        .from('footer_links')
        .insert(link);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-links'] });
      queryClient.invalidateQueries({ queryKey: ['footer-links-all'] });
      toast({
        title: "Link adicionado",
        description: "O link foi adicionado ao rodapé.",
      });
    },
    onError: (error) => {
      console.error('Error adding footer link:', error);
      toast({
        title: "Erro ao adicionar",
        description: "Não foi possível adicionar o link.",
        variant: "destructive",
      });
    },
  });

  // Update footer link
  const updateLinkMutation = useMutation({
    mutationFn: async ({ id, ...link }: Partial<FooterLink> & { id: string }) => {
      const { error } = await supabase
        .from('footer_links')
        .update(link)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-links'] });
      queryClient.invalidateQueries({ queryKey: ['footer-links-all'] });
      toast({
        title: "Link atualizado",
        description: "O link foi atualizado com sucesso.",
      });
    },
    onError: (error) => {
      console.error('Error updating footer link:', error);
      toast({
        title: "Erro ao atualizar",
        description: "Não foi possível atualizar o link.",
        variant: "destructive",
      });
    },
  });

  // Delete footer link
  const deleteLinkMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('footer_links')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['footer-links'] });
      queryClient.invalidateQueries({ queryKey: ['footer-links-all'] });
      toast({
        title: "Link removido",
        description: "O link foi removido do rodapé.",
      });
    },
    onError: (error) => {
      console.error('Error deleting footer link:', error);
      toast({
        title: "Erro ao remover",
        description: "Não foi possível remover o link.",
        variant: "destructive",
      });
    },
  });

  // Filter links by section
  const resourceLinks = footerLinks.filter(link => link.section === 'resources');
  const usefulLinks = footerLinks.filter(link => link.section === 'useful_links');

  return {
    footerSettings,
    footerLinks,
    allFooterLinks,
    resourceLinks,
    usefulLinks,
    isLoading: isLoadingSettings || isLoadingLinks,
    isLoadingAll: isLoadingAllLinks,
    updateSettings: updateSettingsMutation.mutate,
    addLink: addLinkMutation.mutate,
    updateLink: updateLinkMutation.mutate,
    deleteLink: deleteLinkMutation.mutate,
    isUpdating: updateSettingsMutation.isPending || addLinkMutation.isPending || updateLinkMutation.isPending || deleteLinkMutation.isPending,
  };
}
